import { BlurEvent, ContextMenuCreationEvent, DataDisplayEvent, DataSourceExecutionEvent, DataSourceMode, DropdownSelectionEvent, EditRowDecorator, Layout, LookupModelSearchEvent, LookupModelSelectionEvent, Textbox, ValidationResult } from "@mcleod/components";
import { AutogenLayoutProspectAction } from "@mcleod/crm/src/autogen/AutogenLayoutProspectAction";
import { Api, ModelRow, RowUpdateEvent, StringUtil } from "@mcleod/core";
import { EditRowDecoratorProps } from "@mcleod/components/src/page/decorators/EditRowDecoratorProps";
import { ModelObjection } from "./models/ModelObjection";
import { SendInfoPacket } from "./SendInfoPacket";

export class ProspectAction extends AutogenLayoutProspectAction {

    private _contactFilter;

    onLoad(): void | Promise<void> {
        this.textboxActionDate.addlValidationCallback = (value: string, mode: DataSourceMode) => {
            const result:ValidationResult = { component: this.textboxActionDate, isValid: true };
            if (mode !== DataSourceMode.SEARCH) {
                const actionDate = this.activeRow?.getDate(this.textboxActionDate.field, null);
                const now = new Date();
                if (actionDate && actionDate > now) {
                    result.isValid = false;
                    result.validationMessage = "Action Date cannot be in the future.";
                }
            }
            return result;
        };

        this.textboxObjectionId1.quickInfoOverrideSearchMethod = async (layout: Layout, _keyValue: any) => 
            layout.mainDataSource.search({id: _keyValue, type_of: "C"});
        
        this.textboxObjectionId2.quickInfoOverrideSearchMethod = async (layout: Layout, _keyValue: any) => 
            layout.mainDataSource.search({id: _keyValue, type_of: "C"});
        
    }

    sourceProspectActionAfterExecution(event: DataSourceExecutionEvent) {
        if (this.activeRow) {
            if (!this.activeRow.isNull("primary_contact")) {
                this.activeRow.setLookupModelData("primary_contact", { "name": this.activeRow.getString("primary_contact") });
                this.mainDataSource.displayDataInBoundComponent(this.textboxPrimaryContact);
            }
            if (!this.activeRow.isNull("objection_id1")) {
                this.findAndSetObjectionValues("objection_id1", this.textboxObjection1Script);
            }
            if (!this.activeRow.isNull("objection_id2")) {
                this.findAndSetObjectionValues("objection_id2", this.textboxObjection2Script);
            }
        }
    }

    textboxActivityIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            this.activeRow.set("descr", event.selection.getString("descr", null));
            this.mainDataSource.displayDataInBoundComponent(this.textboxDescr);
            this.activeRow.set("info_packet_id", event.selection.getString("info_packet_id", null));
            if (!event.selection.isNull("info_packet_id")) {
                this.applyLookupModelData(this.textboxInfoPacket, this.activeRow).then(() => {
                    this.mainDataSource.displayDataInBoundComponent(this.textboxInfoPacket);
                });
            }
        } else if (event.hasDeselection()) {
            this.textboxDescr.clear();
            this.textboxInfoPacket.clear();
        }
    }

    textboxPrimaryContactBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        if (this.activeRow.isNull("customer_id")) {
            event.preventDefault();
            return;
        }
    }

    textboxCustomerIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const customer = event.selection;
            this.initializeContactFilter(customer.getString("id"));

            this.activeRow.setValueAndLookupModelData(
                "primary_contact",
                customer.getString('contact.name', null),
                { "name": customer.getString('contact.name', null) }
            );
            this.mainDataSource.displayDataInBoundComponent(this.textboxPrimaryContact);
            this.activeRow.setValues({
                "phone": customer.getString('contact.phone', null),
                "email": customer.getString('contact.email', null)
            });
            this.mainDataSource.displayDataInBoundComponent(this.textboxPhone);
            this.mainDataSource.displayDataInBoundComponent(this.textboxEmail);

            if (!customer.isNull("customer_mgr")) {
                this.activeRow.setValueAndLookupModelData(this.textboxOperationsUser.field, customer.getString("customer_mgr"), customer.getLookupModelData("customer_mgr"));
                this.mainDataSource.displayDataInBoundComponent(this.textboxOperationsUser);
            }
            if (!customer.isNull("salesperson_id")) {
                this.activeRow.setValueAndLookupModelData(this.textboxSalespersonId.field, customer.getString("salesperson_id"), customer.getLookupModelData("salesperson_id"));
                this.mainDataSource.displayDataInBoundComponent(this.textboxSalespersonId);
            }
        } else if (event.hasDeselection()) {
            this.removeContactSearchListenerIfNeeded();
            this.textboxPrimaryContact.clear();
            this.textboxPhone.clear();
            this.textboxEmail.clear();
        }
    }

    textboxOperationsUserBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.web_user_type = "U || E"
    }

    textboxPrimaryContactOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const contact = event.selection;
            this.activeRow.setValues({
                "phone": contact.getString('phone', null),
                "email": contact.getString('email', null)
            });
            this.mainDataSource.displayDataInBoundComponent(this.textboxPhone);
            this.mainDataSource.displayDataInBoundComponent(this.textboxEmail);
        } else if (event.hasDeselection()) {
            this.textboxPhone.clear();
            this.textboxEmail.clear();
        }
    }

    textboxStatusOnDataDisplay(event: DataDisplayEvent) {
        this.textboxRemarks.required = ("C" === event.rowData?.getString("status", null));
    }

    textboxStatusAfterDropdownSelection(event: DropdownSelectionEvent) {
        if ("P" === event.selection.value){
            this.activeRow.set("action_date", null);
        } else {
            this.activeRow.set("action_date", new Date());
        }
        this.textboxRemarks.required = ("C" === event.selection.value);
    }

    textboxObjectionId1OnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.textboxObjection1Script.text = event.hasSelection() ? event.getValueFromSelectedRow("response_script") : null;
    }

    textboxObjectionId2OnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.textboxObjection2Script.text = event.hasSelection() ? event.getValueFromSelectedRow("response_script") : null;
    }

    customerTypeSearchFilter(event: LookupModelSearchEvent) {
        event.filter.type_of = "C";
    }

    notReccallSearchFilter(event: LookupModelSearchEvent) {
        event.filter.id = "!RECCALL";
    }

    customerActivityIdSearchFilter(event: LookupModelSearchEvent) {
        this.customerTypeSearchFilter(event);
        this.notReccallSearchFilter(event);
    }

    textboxActivityIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.customerActivityIdSearchFilter(event);
    }

    textboxNextActIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.customerActivityIdSearchFilter(event);
    }

    async textboxNextActionAfterDropdownSelection(event: DropdownSelectionEvent) {
        await this.syncNextActivity();
        if (event.hasSelection()) {
            this.textboxNextActId.required = true;
            this.textboxNextActDate.required = true;
            switch (event.selection.value) {
                case "A":
                    this.activeRow.setValues({
                        "next_act_id": null,
                        "next_act_date": null
                    });
                    this.textboxNextActId.clear();
                    this.textboxNextActDate.clear();
                    this.textboxNextActId.enabled = true;
                    this.textboxNextActDate.enabled = true;
                    break;
                case "U":
                    this.textboxNextActId.enabled = false;
                    this.textboxNextActDate.enabled = true;
                    break;
            }
        } else if (event.hasDeselection()) {
            this.textboxNextActId.enabled = this.textboxNextActId.required = false;
            this.textboxNextActDate.enabled = this.textboxNextActDate.required = false;
        }
    }

    private findAndSetObjectionValues(targetField: string, targetComponent: Textbox) {
        const objectionId = this.activeRow.getString(targetField);
        new ModelObjection().searchSingle({ id: objectionId, "type_of": "C" })
            .then((modelRow: ModelRow) => {
                this.activeRow.setLookupModelData(targetField, modelRow);
                targetComponent.text = modelRow.getString("response_script");
                this.mainDataSource.displayDataInBoundComponents();
            })
    }

    initializeContactFilter(customerId: string) {
        this.removeContactSearchListenerIfNeeded();
        this._contactFilter = (event: LookupModelSearchEvent) => {
            event.filter.is_active = 'Y';
            event.filter.parent_row_type = 'C';
            event.filter.parent_row_id = customerId;
        };
        this.textboxPrimaryContact.addBeforeLookupModelSearchListener(this._contactFilter);
    }

    private removeContactSearchListenerIfNeeded() {
        if (this._contactFilter) {
            this.textboxPrimaryContact.removeBeforeLookupModelSearchListener(this._contactFilter);
        }
    }

    textboxDateDueOnBlur(event: BlurEvent) {
        if (event.changedWhileFocused) {
            this.syncNextActivity();
        }
    }

    textboxCustomerIdOnBlur(event: BlurEvent) {
        if (event.changedWhileFocused) {
            this.syncNextActivity();
        }
    }

    syncNextActivity() {
        const actionId = this.activeRow.getString("id", null);
        const customerId = this.activeRow.getString("customer_id", null);
        const dateDue = this.activeRow.getDate("date_due", null);
        const opportunityId = this.activeRow.get("opportunity_id", null);
        return ProspectAction.findNextActivity(actionId, customerId, dateDue, opportunityId).then((nextActivity) => {
            if (nextActivity.id) {
                this.activeRow.setValueAndLookupModelData("next_act_id", nextActivity.id, { "id": nextActivity.id, "descr": nextActivity.descr });
                this.activeRow.set("next_act_date", nextActivity.date_due);
            } else {
                this.activeRow.setValues({
                    "next_act_id": null,
                    "next_act_date": null
                });
            }
            this.textboxNextActId.displayComponentData(this.activeRow, null, 0);
            this.textboxActionDate.displayComponentData(this.activeRow, null, 0);
        });
    }

    static findNextActivity(actionId:string, customerId: string, dateDue: Date, opportunityId: string): Promise<{id: string, descr: string, date_due: Date}> {
        if (StringUtil.isEmptyString(customerId) || !dateDue) {
            return Promise.resolve({id: null, descr: null, date_due: null});
        }
        return Api.post("lme/crm/next-activity", {
            "action_id": actionId,
            "customer_id": customerId,
            "opportunity_id": opportunityId,
            "date_due": dateDue
        }).then((response) => {
            if (response.data && response.data.length > 0) {
                return {
                    id: response.data[0].activity_id,
                    descr: response.data[0].descr,
                    date_due: new Date(response.data[0].date_due)
                };
            }
            return {id: null, descr: null, date_due: null};
        });
    }

    static showAddActionSlideout(defaults: Partial<any>, props?: Partial<EditRowDecoratorProps>) {
        const prospectActionLayout = Layout.getLayout("lme/crm/ProspectAction") as ProspectAction;

        const decorator = new EditRowDecorator({
            layout: prospectActionLayout,
            title: "Add Prospect Activity",
            fillHeight: true,
            layoutLoadListeners: [(_) => prospectActionLayout.mainDataSource.createBlankRow().then(row => {
                prospectActionLayout.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [row]);
                prospectActionLayout.activeRow.setValues(defaults);
                prospectActionLayout.syncNextActivity();
                decorator.data = prospectActionLayout.activeRow;
                row.addAfterPostListener((event: RowUpdateEvent) => SendInfoPacket.checkInfoPacket(event.row));
            })],
            ...props
        });
    }

    static showEditActionSlideout(modelRow: ModelRow<any>, props?: Partial<EditRowDecoratorProps>) {
        const prospectActionId = modelRow.get("prospect_action_id", modelRow.get("id", null)) as string;
        if (StringUtil.isEmptyString(prospectActionId)) {
            throw new Error("Prospect Action ID is required to edit an action.");
        }
        const prospectActionLayout = Layout.getLayout("lme/crm/ProspectAction") as ProspectAction;
        const decorator = new EditRowDecorator({
            layout: prospectActionLayout,
            title: "Edit Prospect Action",
            fillHeight: true,
            layoutLoadListeners: [(_) => prospectActionLayout.search({ id: prospectActionId }).then(() => {
                if (prospectActionLayout.activeRow.isNull("action_date")) {
                    prospectActionLayout.activeRow.set("status", "C");
                    prospectActionLayout.activeRow.set("action_date", new Date());
                }
                prospectActionLayout.initializeContactFilter(modelRow.getString("customer_id"));
                prospectActionLayout.activeRow.setLookupModelData("activity_id", {id: modelRow.getString("activity_id")});
                prospectActionLayout.mainDataSource.displayDataInBoundComponent(prospectActionLayout.textboxActivityId);
                decorator.dataSource = prospectActionLayout.mainDataSource;
                decorator.data = prospectActionLayout.activeRow;
                prospectActionLayout.syncNextActivity();
                prospectActionLayout.activeRow.addAfterPostListener((event: RowUpdateEvent) => SendInfoPacket.checkInfoPacket(event.row));

            })],
            ...props,
            onDelete: () => {
                decorator.slideOut();
                decorator.data.delete().then(() => {
                    if (props?.onDelete){
                        props.onDelete();
                    }
                })
            }
        });
    }

    private applyLookupModelData(textbox: Textbox, modelRow: ModelRow): Promise<void> {
        const lookupLayout = Layout.getLayout(textbox.lookupModelLayout);
        return lookupLayout.ensureLoaded().then(() => {
            const fieldValue = modelRow.get(textbox.field);
            const filter = {
                [textbox.lookupModelResultField]: (fieldValue.indexOf(",") >= 0) ? `in ${fieldValue}` : fieldValue
            };
            return lookupLayout.mainDataSource.search(filter, null, { layoutName: lookupLayout.layoutName }).then(response => {
                if (response?.modelRows?.length === 1) {
                    modelRow.setValueAndLookupModelData(textbox.field, modelRow.get(textbox.field), response.modelRows[0]);
                } else if (response?.modelRows?.length > 1) {
                    modelRow.setValueAndLookupModelData(textbox.field, modelRow.get(textbox.field), response.modelRows.map(row => ({
                        [textbox.lookupModelDisplayField]: row.get(textbox.lookupModelDisplayField),
                        [textbox.lookupModelResultField]: row.get(textbox.lookupModelResultField)
                    })));
                } else {
                    const lookupModelData = new ModelRow(modelRow._modelPath, false);
                    lookupModelData.set(textbox.lookupModelResultField, modelRow.get(textbox.field));
                    lookupModelData.set(textbox.lookupModelDisplayField, modelRow.get(textbox.field));
                    modelRow.setValueAndLookupModelData(textbox.field, modelRow.get(textbox.field), lookupModelData);
                }
            });
        })
    }
}
